import { createFileRoute } from "@tanstack/react-router";
import { assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import { cameraToTHREE, mesh3ToTHREE, scaleBufferGeometryUVs } from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { initPhysicallyBasedRenderer, initTHREEJSRenderer } from "gfx/initPhysicallyBasedRenderer";
import { initWebGLContext } from "gfx/initWebGLContext";
import { designMaterialNew } from "gfx/materials/designMaterial";
import type { DesignMaterialLoadOptions } from "gfx/materials/designMaterialRuntimeConfig";
import { defaultDesignMaterialLoadOptions } from "gfx/materials/designMaterialRuntimeConfig";
import { cloneDefaultMaterialParams } from "gfx/materials/designMaterialUtils";
import { loadAllMaterialEditorTextures } from "gfx/materials/materialEditorTextures";
import { envPresetSceneLights } from "gfx/sceneLights";
import { RenderParamsSource } from "gfx/threejs/higharc/RenderParamsSource";
import type { RenderTargetPool } from "gfx/threejs/higharc/RenderTargetPool";
import { SSFXRendererOutput } from "gfx/threejs/higharc/SSFXRendererOutput";
import type { SSFXRendererParamsManager } from "gfx/threejs/higharc/SSFXRendererParams";
import SSFXRendererScene from "gfx/threejs/higharc/SSFXRendererScene";
import type { SSFXRenderTarget } from "gfx/threejs/higharc/SSFXRenderTarget";
import { WebGLExtendedCapabilities } from "gfx/WebGLExtendedCapabilities";
import getPageTitle from "lib/utils/getPageTitle";
import type { MutableRefObject, JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { testCubeMesh } from "test/util/geometryGenerator";
import * as THREE from "three";
import { TextWithHighlights } from "ui/text/TextWithHighlights";

interface TestStep {
  enable: (
    renderer: THREE.WebGLRenderer,
    scene: SSFXRendererScene,
    fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
  ) => Promise<void>;
}

const cameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 20,
  y: 12,
  z: 40,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 0,
  upZ: 1,
  cameraNear: 5,
  cameraFar: 100,
} as const;

const viewerAspect = 1;
const camera = cameraToTHREE(cameraSettings, viewerAspect);

const testSteps: TestStep[] = [
  {
    enable: async (
      renderer: THREE.WebGLRenderer,
      scene: SSFXRendererScene,
      fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      const sceneLights = {
        ...envPresetSceneLights[EnvPreset.WHITEBOX],
        interiorAmbientLight: undefined, // disable the interior ambient light
      };
      await scene.sceneLightsManager.renderSceneLights(sceneLights, {});
      scene.scene.add(scene.sceneLightsManager.group);

      scene.scene.add(camera);

      scene.updateRenderParams({
        renderPipelineParams: {
          output: SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS,
        },
      });

      fetchedTexturesRef.current = await loadAllMaterialEditorTextures();
      const materialParams = cloneDefaultMaterialParams();
      materialParams.color = "#55ff33";
      materialParams.layers[0].enabled = true;
      materialParams.layers[0].blendMode = "multiply";
      materialParams.layers[0].useForHeight = true;
      materialParams.layers[0].heightInfluence = 0.2;
      materialParams.sdf.common.maskOnly = false;
      materialParams.sdf.common.useShadows = true;
      materialParams.sdf.common.useAO = true;
      materialParams.sdf.common.depth = 0.3;
      materialParams.sdf.patterns[0].enabled = true;
      materialParams.sdf.patterns[0].xDistance = 5.2;
      materialParams.sdf.patterns[0].yDistance = 3.2;
      materialParams.sdf.patterns[1].enabled = true;
      materialParams.sdf.patterns[1].xDistance = 5.2;
      materialParams.sdf.patterns[1].yDistance = 3.2;
      materialParams.sdf.patterns[1].yOffset = 1.6;
      materialParams.sdf.patterns[1].xOffset = 2.6;
      const loadOptions: DesignMaterialLoadOptions = {
        ...defaultDesignMaterialLoadOptions,
        disableSDF: false,
        disableLayers: false,
        useWebgl2TextureAtlas: renderer.capabilities.isWebGL2,
        reproduceImplicitLodIntelBug: true, // critical for reproducing the bug.
      };
      const material = await designMaterialNew(
        materialParams,
        renderer,
        (url, _overrideFormat) => [ensureDefined(fetchedTexturesRef.current)[url], Promise.resolve()],
        true,
        undefined,
        loadOptions,
      );

      const TEST_MESH_ID = 1;
      const cubeMesh3 = testCubeMesh(2, TEST_MESH_ID);
      const cube = mesh3ToTHREE(cubeMesh3, material);
      scene.scene.add(cube);

      const planeGeom = new THREE.PlaneGeometry(20, 20);
      scaleBufferGeometryUVs(planeGeom, 20.0);
      const plane = new THREE.Mesh(planeGeom, material);
      plane.position.set(0, 0, -2);
      plane.updateMatrixWorld();
      scene.scene.add(plane);
    },
  },
];

function GraphicsTroubleshootingTestPage(): JSX.Element {
  const [rendererInfo, setRendererInfo] = useState<string>("");
  const [testStepIndex, setTestStepIndex] = useState<number>(0);
  const [log, setLog] = useState<string>("");

  const canvasParentRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTargetRef = useRef<SSFXRenderTarget | undefined>(undefined);
  const renderTargetPoolRef = useRef<RenderTargetPool | undefined>(undefined);
  const ssfxParamsManagerRef = useRef<SSFXRendererParamsManager | undefined>(undefined);
  const rendererRef = useRef<THREE.WebGLRenderer | undefined>(undefined);
  const ssfxSceneRef = useRef<SSFXRendererScene | undefined>(undefined);
  const fetchedTexturesRef = useRef<{ [name: string]: THREE.Texture } | null>(null);

  useEffect(() => {
    const contextInfo = initWebGLContext({
      framebufferOptions: {
        antialias: false,
        depthStencil: false,
      },
      throwOnGLError: false,
      logGLResourceUsage: false,
      label: "Testing",
    });
    const ext = contextInfo.glContext.getExtension("WEBGL_debug_renderer_info");
    let unmaskedRenderer;
    if (!ext) {
      setRendererInfo("Active GPU Renderer: WEBGL_debug_renderer_info not supported");
    } else {
      unmaskedRenderer = contextInfo.glContext.getParameter(ext.UNMASKED_RENDERER_WEBGL);
      setRendererInfo("Active GPU Renderer: " + unmaskedRenderer);
    }
    if (renderTargetRef.current && canvasRef.current) {
      // Reset state on hot reload
      renderTargetRef.current.dispose();
      renderTargetPoolRef.current?.dispose();
      canvasParentRef.current?.removeChild(canvasRef.current);
    }
    canvasRef.current = contextInfo.canvas;
    canvasRef.current.width = 500;
    canvasRef.current.height = 500;
    const renderer = initTHREEJSRenderer(contextInfo.canvas, contextInfo.glContext, contextInfo.glContextAttributes);
    const capabilities = new WebGLExtendedCapabilities({ renderer });
    const { screenRenderTarget, ssfxParamsManager, renderTargetPool } = initPhysicallyBasedRenderer({
      renderer,
      capabilities,
      userOptions: {
        ssfxRenderer: true,
        useMSAA: false,
      },
    });
    assertIsNotUndefined(screenRenderTarget);
    assertIsNotUndefined(ssfxParamsManager);
    ssfxParamsManager.setRenderParameters({
      screenRenderParams: {
        ssaaEnabled: false,
        reprojectionTAAEnabled: false,
      },
      id: 0,
      source: RenderParamsSource.TESTING,
    });
    renderTargetRef.current = screenRenderTarget;
    ssfxParamsManagerRef.current = ssfxParamsManager;
    renderTargetPoolRef.current = renderTargetPool;
    rendererRef.current = renderer;
    screenRenderTarget.setFinalRenderSize(canvasRef.current.width, canvasRef.current.height);
    assertIsNotUndefined(canvasParentRef.current);
    canvasParentRef.current?.appendChild(canvasRef.current);

    const threeScene = new THREE.Scene();
    ssfxSceneRef.current = new SSFXRendererScene({ scene: threeScene });

    screenRenderTarget.setScene(ssfxSceneRef.current);
    screenRenderTarget.setCamera(camera);

    // Reset state on hot reload
    setTestStepIndex(0);
    setLog("");
  }, []);

  async function runStep() {
    if (testStepIndex >= testSteps.length) {
      return;
    }
    const renderTarget = renderTargetRef.current;
    if (!renderTarget) {
      return;
    }
    const ssfxParamsManager = ssfxParamsManagerRef.current;
    if (!ssfxParamsManager) {
      return;
    }
    const renderer = rendererRef.current;
    if (!renderer) {
      return;
    }
    const scene = ssfxSceneRef.current;
    if (!scene) {
      return;
    }
    await testSteps[testStepIndex].enable(renderer, scene, fetchedTexturesRef);
    ssfxParamsManager.setupParamsForRender(scene, camera);
    renderTarget.beginFrame({
      ssaaEnabled: false,
      ssaaTemporalSampleLevel: 1,
      reprojectionTAAEnabled: false,
      renderResolutionMultiplier: 1.0,
    });
    scene.updateShadowMap(renderer, camera);
    renderTarget.applyRenderParams(ssfxParamsManager.getRenderParams());
    renderTarget.accumulateSampleIfNeeded();
    renderTarget.renderSSAAOutput(null);
    setLog(log + "\nCompleted step: " + testStepIndex);
    setTestStepIndex(testStepIndex + 1);
  }

  const testStepsRemaining = testStepIndex < testSteps.length;

  return (
    <>
      <Head>
        <title>{getPageTitle("Graphics Troubleshooting")}</title>
      </Head>
      <style>
        {`
        .glInfo, .testParent, .docsParent {
          margin: 20px auto;
          max-width: 50em;
        }
        .highlight {
          font-weight: bold;
          background-color: transparent;
        }
        .warning {
          padding: 10px;
          background: #ffeeaa;
          border: 1px solid #ffaaaa;
        }
        .inline-header {
          font-weight: bold;
        }
        `}
      </style>
      <div className="glInfo">
        <h2>Repro case for bug on some Intel drivers</h2>
        <p>System information:</p>
        <div className="infoString">
          <TextWithHighlights
            text={rendererInfo}
            highlightedStrings={["AMD", "Intel", "NVIDIA"]}
            highlightClassName="highlight"
          />
        </div>
      </div>
      <div className="testParent">
        <h2>Manual test</h2>
        <p>Run the test by pressing the button below. It is expected to reproduce a bug on Intel GPU drivers.</p>
        {renderTargetRef.current !== undefined ? (
          <div>
            <div>
              <button disabled={!testStepsRemaining} onClick={runStep}>
                {testStepsRemaining ? `Run test (${testStepIndex} / ${testSteps.length} steps completed)` : "Test done"}
              </button>
            </div>
            <pre>{log}</pre>
          </div>
        ) : (
          ""
        )}
        <div ref={canvasParentRef}></div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/intel-driver-bug-repro-2024")({
  component: GraphicsTroubleshootingTestPage,
});
